import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Base_Url } from '../config/domainConfig';
import './ApplicationPeriodsSection.css';

const ApplicationPeriodsSection = () => {
  const [activePeriod, setActivePeriod] = useState(null);
  const [latestResults, setLatestResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  const API_BASE_URL = `${Base_Url}/api`;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch active application period
      const periodResponse = await fetch(`${API_BASE_URL}/application-periods/active`);
      const periodData = await periodResponse.json();
      
      if (periodData.success) {
        setActivePeriod(periodData.data);
      }

      // Fetch latest funding results
      const resultsResponse = await fetch(`${API_BASE_URL}/funding-results/latest`);
      const resultsData = await resultsResponse.json();
      
      if (resultsData.success) {
        setLatestResults(resultsData.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <section id="application-periods" className="section">
        <div className="container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="application-periods" className="section">
      <div className="container">
        <h2 className="section-title">{t('periods.title')}</h2>
        
        <div className="periods-content">
          {/* Current Application Period */}
          {activePeriod && (
            <div className="current-period">
              <div className="period-header">
                <h3>{t('periods.current_period')}</h3>
                <div className="period-status active">{t('periods.active')}</div>
              </div>
              <div className="period-details">
                <h4>{activePeriod.title}</h4>
                <p className="period-description">{activePeriod.description}</p>
                <div className="period-dates">
                  <div className="date-item">
                    <span className="date-label">{t('periods.start_date')}</span>
                    <span className="date-value">{formatDate(activePeriod.start_date)}</span>
                  </div>
                  <div className="date-item">
                    <span className="date-label">{t('periods.end_date')}</span>
                    <span className="date-value">{formatDate(activePeriod.end_date)}</span>
                  </div>
                </div>
                <div className="period-cta">
                  <a href="#home" className="apply-now-btn">
                    {t('periods.apply_now')}
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Latest Funding Results */}
          {latestResults.length > 0 && (
            <div className="funding-results">
              <div className="results-header">
                <h3>{t('periods.latest_results')}</h3>
                <p>{t('periods.congratulations')}</p>
              </div>
              
              <div className="results-grid">
                {latestResults.map((result, index) => (
                  <div key={result.id} className="result-card">
                    <div className="result-header">
                      <div className="result-rank">#{index + 1}</div>
                      <div className="result-category">{result.business_category}</div>
                    </div>
                    
                    <div className="result-content">
                      <h4 className="applicant-name">{result.applicant_name}</h4>
                      {result.company_name && (
                        <p className="company-name">{result.company_name}</p>
                      )}
                      <p className="result-description">{result.description}</p>
                      
                      <div className="funding-amount">
                        <span className="amount-label">{t('periods.funding_received')}</span>
                        <span className="amount-value">{formatAmount(result.funding_amount)}</span>
                      </div>
                    </div>
                    
                    <div className="result-footer">
                      <span className="period-name">{result.period_title}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Active Period Message */}
          {!activePeriod && latestResults.length === 0 && (
            <div className="no-data">
              <div className="no-data-icon">📋</div>
              <h3>{t('periods.no_active_period')}</h3>
              <p>{t('periods.no_period_message')}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ApplicationPeriodsSection; 